import { z } from 'zod';

import { SLICE_TAG } from '@/constants';

import { apiSlice } from '../api-slice';
import {
    ResponseOrderListSchema,
    ResponseOrderSchema,
    ResponseSingleOrderSchema,
} from './order.schema';

export const orderApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createOrder: builder.mutation({
            query: (data) => ({
                url: 'orders/confirm',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE, SLICE_TAG.SALES],
        }),

        getAllOrders: builder.query({
            query: (params) => {
                const {
                    payment_status,
                    customer_type,
                    payment_type,
                    customer_id,
                    end_date,
                    ...rest
                } = params;
                return {
                    url: 'orders',
                    method: 'GET',
                    params: {
                        ...rest,
                        ...(payment_status ? { payment_status } : {}),
                        ...(customer_id ? { customer_id } : {}),
                        ...(customer_type ? { customer_type } : {}),
                        ...(payment_type ? { payment_type } : {}),
                        ...(end_date ? { end_date } : {}),
                    },
                };
            },
            providesTags: [SLICE_TAG.SALES],

            keepUnusedDataFor: 600,

            transformResponse: (response) => {
                return ResponseOrderSchema.parse(response);
            },
        }),

        getAllOrdersV2: builder.query({
            query: (params) => {
                return {
                    url: 'orders/v2',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.SALES],

            keepUnusedDataFor: 600,

            transformResponse: (response) => {
                return ResponseOrderListSchema.parse(response);
            },
        }),

        getSingleOrder: builder.query({
            query: (id) => `orders/${id}`,
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.SALES, id },
            ],
            transformResponse: (response) => {
                return ResponseSingleOrderSchema.parse(response);
            },
        }),

        getOrderById: builder.query<
            z.infer<typeof ResponseSingleOrderSchema>,
            string
        >({
            query: (id) => `orders/${id}`,
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.SALES, id },
            ],
            transformResponse: (response) => {
                return ResponseSingleOrderSchema.parse(response);
            },
        }),

        payForAnOrderById: builder.mutation({
            query: (data) => {
                const { id, ...rest } = data;

                return {
                    url: `orders/${id}/pay`,
                    method: 'PUT',
                    body: rest,
                };
            },
            invalidatesTags: (_result, _error, data) => [
                { type: SLICE_TAG.SALES, id: data.id },
                SLICE_TAG.SALES,
            ],
        }),

        multiPayForOrderById: builder.mutation({
            query: (payload) => {
                const { id, ...rest } = payload;

                return {
                    url: `orders/${id}/multiple-pay`,
                    method: 'PUT',
                    body: rest,
                };
            },
            invalidatesTags: (_result, _error, arg) => [
                { type: SLICE_TAG.SALES, id: arg.id },
                SLICE_TAG.SALES,
            ],
        }),

        payForMultiOrder: builder.mutation({
            query: (data) => {
                return {
                    url: `orders/pay-orders`,
                    method: 'PUT',
                    body: data,
                };
            },
            invalidatesTags: (_result, _error, data) => [
                { type: SLICE_TAG.SALES, id: data.id },
                SLICE_TAG.SALES,
            ],
        }),

        getOrderByCustomerId: builder.query({
            query: (id) => {
                return {
                    url: `orders/customers/${id}`,
                    method: 'GET',
                };
            },
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.SALES, id },
            ],
            // transformResponse: (response) => {
            //   return ResponseOrderSchema.parse(response);
            // },
        }),
    }),
});

export const {
    useCreateOrderMutation,
    useGetAllOrdersQuery,
    useLazyGetAllOrdersQuery,
    useLazyGetSingleOrderQuery,
    useGetSingleOrderQuery,
    usePayForAnOrderByIdMutation,
    usePayForMultiOrderMutation,
    useGetOrderByCustomerIdQuery,
    useLazyGetOrderByCustomerIdQuery,
    useGetOrderByIdQuery,
    useLazyGetOrderByIdQuery,
    useGetAllOrdersV2Query,
    useLazyGetAllOrdersV2Query,
    useMultiPayForOrderByIdMutation,
} = orderApi;
